import React, {ReactNode} from 'react'
import styled from 'styled-components'
import {Box} from '@mui/material'
import {ApplyTitle, FirstPageDiv, HeaderArea, HeaderAreaBox, NextPageDiv} from './NatAdminApplicatioPrintCommonStyle'
import {getPrintDate} from '../../helper/NatApplicationHelper'
import {StyledDiv} from '../../../../../../styledComponents/StyledTypeDefinitions'
import {NatSealField, SealField} from './NatSealField'
import {NatEncloseInParenthesis} from './NatEncloseInParenthesis'
import moment from 'moment'
import {DESTINATION_TYPE, RESIDENT_APPLY_PROCESS, RESIDENT_APPLY_STATUS} from '../../../../../../CodeMaster'
import {ApplyStatusInfo} from '../../../repository/NatGetApplicationStatus'
import {convertToRoomNumberForResident, getFormattedZipCode} from '../../../../../common/util/Natutil'
import {Nullable} from '../../../../../../app/TypeHelpers'
import {NatLongAbsenceDetail} from '../../../repository/NatGetLongAbsence'

interface Props {
    longAbsenceApplicationDocumentParam: NatLongAbsenceDetail
    applyStatusInfoList: ApplyStatusInfo[]
}

export const NatLongAbsenceApplicationDocument: React.FC<Props> = props => {
    const {
        longAbsenceApplicationDocumentParam,
        applyStatusInfoList
    } = props

    const {
        applyId, condominiumName, ridgeName, roomNumber,
        applicantFamilyName, applicantFirstName, absenceStartDate, absenceEndDate,
        applicantContactPhoneNumber, destinationType, destinationZipCode, destinationPrefecture,
        destinationAddress2, destinationAddress3, destinationBuildingRoomNumber, destinationAddress,
        contactFamilyName, contactFirstName, contactRelationship, contactZipCode,
        contactPrefecture, contactAddress2, contactAddress3, contactBuildingRoomNumber,
        contactPhoneNumber,
    } = longAbsenceApplicationDocumentParam

    const applicantRoom = `${ridgeName} ${convertToRoomNumberForResident(roomNumber)}`
    const applicantName = `${applicantFamilyName} ${applicantFirstName}`
    const destinationTypeLabel = `${DESTINATION_TYPE.codeToEnumConstants(destinationType).label}`
    const contactName = `${contactFamilyName} ${contactFirstName}`
    const destinationZipCodeValue = destinationZipCode ?? ''
    const contactZipCodeValue = contactZipCode ?? ''

    return (<>
        <FirstPageDiv>
            <HeaderArea>
                <HeaderAreaBox>
                    <Box ml={'auto'} mt={'10mm'}>申請ID：{applyId}</Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box m={'auto'}><ApplyTitle>長期不在届</ApplyTitle></Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box ml={'auto'} mt={'3mm'}>
                        {renderSealField(applyStatusInfoList)}
                    </Box>
                </HeaderAreaBox>
            </HeaderArea>
            <Box ml={'25mm'} mt={'3mm'}>
                理事長殿
            </Box>
            <Box ml={'30mm'} mt={'5mm'} whiteSpace={'pre-wrap'}>
                　私は、連続して1か月以上の長期間にわたり不在となりますので、次のとおり提出をいたします。
            </Box>
            <Box margin={'3mm 0 0 30mm'} display={'flex'} flexDirection={'column'} width={'130mm'}>
                <Box>
                    <Box mb={'5mm'}><HeadLineStyled>1  申請者</HeadLineStyled></Box>
                    <Box ml={'8mm'} mb={'5mm'}>
                        {renderApplicationDetail('マンション名', condominiumName)}
                        {renderApplicationDetail('部屋番号', applicantRoom)}
                        {renderApplicationDetail('氏名', applicantName)}
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'}><HeadLineStyled>2  不在期間</HeadLineStyled></Box>
                    <Box ml={'8mm'} mb={'5mm'}>
                        {renderApplicationDetail('開始日', renderBrowseApplyDate(absenceStartDate))}
                        {renderApplicationDetail('終了日', renderBrowseApplyDate(absenceEndDate))}
                    </Box>
                </Box>
                {(destinationType === DESTINATION_TYPE.DOMESTIC.value) &&
                    <>
                        <Box>
                            <Box mb={'5mm'}><HeadLineStyled>3  不在中の連絡先（本人）</HeadLineStyled></Box>
                            <Box ml={'8mm'} mb={'5mm'}>
                                {renderApplicationDetail('滞在先', destinationTypeLabel)}
                                {renderApplicationDetail('郵便番号', getFormattedZipCode(destinationZipCodeValue, false))}
                                {renderApplicationDetail('都道府県', destinationPrefecture)}
                                {renderApplicationDetail('市区町村', destinationAddress2)}
                                {renderApplicationDetail('番地', destinationAddress3)}
                                {renderApplicationDetail('建物名・部屋番号', destinationBuildingRoomNumber)}
                                {renderApplicationDetail('電話番号', applicantContactPhoneNumber)}
                            </Box>
                        </Box>
                        <Box>
                            <Box mb={'5mm'}>
                                <HeadLineStyled>
                                    4  国外に滞在する場合は、国内連絡先として申請いただいた連絡先あてに通知します。<br/>
                                    本届の提出がない場合は、専有部分の所在地あてに通知します。<br/>
                                    ただし、占有者に対しては、当該住戸を所有する組合員の連絡先とします。
                                </HeadLineStyled>
                            </Box>
                        </Box>
                        <Box>
                            <Box mb={'5mm'}>
                                <HeadLineStyled>
                                    5  本届の不備及び未提出に起因する事故等については、管理組合は責任を負いません。
                                </HeadLineStyled>
                            </Box>
                        </Box>
                    </>
                }
                {(destinationType === DESTINATION_TYPE.OVERSEA.value) &&
                    <Box>
                        <Box mb={'5mm'}><HeadLineStyled>3  不在中の連絡先（本人）</HeadLineStyled></Box>
                        <Box ml={'8mm'} mb={'5mm'}>
                            {renderApplicationDetail('滞在先', destinationTypeLabel)}
                            <PrintApplicationDetailContainer>
                                <Box>{'住所'}</Box>
                                <PrintApplicationParenthesisContainer>
                                    <StyledCardContent>
                                        {destinationAddress}
                                    </StyledCardContent>
                                </PrintApplicationParenthesisContainer>
                            </PrintApplicationDetailContainer>
                            {renderApplicationDetail('電話番号', applicantContactPhoneNumber)}
                        </Box>
                    </Box>
                }
            </Box>
        </FirstPageDiv>
        {(destinationType === DESTINATION_TYPE.OVERSEA.value) &&
            <NextPageDiv>
                <HeaderArea>
                    <HeaderAreaBox>
                        <Box ml={'auto'} mt={'10mm'}>申請ID：{applyId}</Box>
                    </HeaderAreaBox>
                    <HeaderAreaBox>
                        <Box m={'auto'}><ApplyTitle>長期不在届</ApplyTitle></Box>
                    </HeaderAreaBox>
                </HeaderArea>
                <Box margin={'3mm 0 0 30mm'} display={'flex'} flexDirection={'column'} width={'130mm'}>
                    <Box>
                        <Box mb={'5mm'}><HeadLineStyled>4  国内の連絡先</HeadLineStyled></Box>
                        <Box ml={'8mm'} mb={'5mm'}>
                            {renderApplicationDetail('氏名', contactName)}
                            {renderApplicationDetail('続柄', contactRelationship)}
                            {renderApplicationDetail('郵便番号', getFormattedZipCode(contactZipCodeValue, false))}
                            {renderApplicationDetail('都道府県', contactPrefecture)}
                            {renderApplicationDetail('市区町村', contactAddress2)}
                            {renderApplicationDetail('番地', contactAddress3)}
                            {renderApplicationDetail('建物名・部屋番号', contactBuildingRoomNumber)}
                            {renderApplicationDetail('電話番号', contactPhoneNumber)}
                        </Box>
                    </Box>
                    <Box>
                        <Box mb={'5mm'}>
                            <HeadLineStyled>
                                5  国外に滞在する場合は、国内連絡先として申請いただいた連絡先あてに通知します。<br/>
                                本届の提出がない場合は、専有部分の所在地あてに通知します。<br/>
                                ただし、占有者に対しては、当該住戸を所有する組合員の連絡先とします。
                            </HeadLineStyled>
                        </Box>
                    </Box>
                    <Box>
                        <Box mb={'5mm'}>
                            <HeadLineStyled>
                                6  本届の不備及び未提出に起因する事故等については、管理組合は責任を負いません。
                            </HeadLineStyled>
                        </Box>
                    </Box>
                </Box>
            </NextPageDiv>
        }
    </>)

}
NatLongAbsenceApplicationDocument.displayName = 'NatLongAbsenceApplicationDocument'

const renderApplicationDetail = (title: string, value: Nullable<string | ReactNode>) => {
    return (
        <PrintApplicationDetailContainer>
            <Box whiteSpace={'pre-wrap'}>{title}</Box>
            <NatEncloseInParenthesis item={value ?? ''}/>
        </PrintApplicationDetailContainer>
    )
}

const renderBrowseApplyDate = (date: string) => {
    const value = getPrintDate(date)

    return (
        <>
            <Box component={'span'} padding={'0 3mm 0 1mm'}>{value.year}</Box>年
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.month}</Box>月
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.day}</Box>日
        </>
    )
}

const renderSealField = (applyStatusInfoList: ApplyStatusInfo[]) => {
    const sealFields: SealField[] = applyStatusInfoList.map((info) => {
        const applyStatusEnum = RESIDENT_APPLY_PROCESS.codeToEnumConstants(info.applyProcess)
        let seal
        if (info.applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) {
            seal = '却下'
        } else {
            seal = info.actual.registerDatetime ? moment(info.actual.registerDatetime).format('YYYY/M/D') : ''
        }
        return {
            applyStatus: applyStatusEnum.label,
            seal: seal,
            sortOrder: applyStatusEnum.displayOrder
        }
    })

    return (
        <NatSealField sealFields={sealFields}/>
    )
}

const PrintApplicationDetailContainer: StyledDiv = styled.div`
  display: flex;
  width: 100%;
`

const PrintApplicationParenthesisContainer: StyledDiv = styled.div`
  display: flex;
  height: 100%;
  width: 90mm;
  margin-left: auto;
`

const StyledCardContent = styled.div`
  border: solid 0.5pt black;
  width: 100%;
  min-height: 25mm;
  height: 100%;
  padding: 1mm 1mm 5mm 1mm;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  margin-bottom: 1mm;
`

const HeadLineStyled = styled.div`
  white-space: pre-wrap;
  padding-left: 1.3em;
  text-indent: -1.3em;
`
